import React, { useState, useEffect, useRef, useContext } from "react";
import {
  FileText, Download, Upload, Eye, Share, Trash2, Search, Filter,
  ArrowDown, Grid, List, Star, Clock, User, Folder, File, Image,
  FileSpreadsheet, FileImage, Archive, Plus, MoreHorizontal, Edit,
  Copy, ExternalLink, Send, CheckCircle, XCircle, Clock as ClockIcon,
  DollarSign, TrendingUp, Wallet, AlertCircle, Calendar,
} from "lucide-react";
import { ThemeContext } from "../theme";
import AlertModal from "../../components/AlertModal";
import { LanguageContext } from "../LanguageContext";
import ReusableDropdown from "../../components/ReusableDropdown";
import { apiFetch } from "../../context/AuthContext";

// ── translations ──────────────────────────────────────────────────────────────
import translations from './constants/translations';
// ── constants ─────────────────────────────────────────────────────────────────
import { getDocumentTypes } from "./constants/documentTypes";
import { getUrgencyLevels } from "./constants/urgencyLevels";

// ── utils ─────────────────────────────────────────────────────────────────────
import { formatCurrency, formatDate, formatFileSize, calculateTotalStorage } from "./utils/formatters";
import { getStatusColor, getStatusIcon, getFileIcon, getUrgencyColor } from "./utils/statusHelpers";
import { transformDocumentData } from "./utils/dataTransformers";
import { buildStyles } from "./utils/styles";
import { useResponsive } from "./utils/useResponsive";
import { useProfile } from "../../hooks/useProfile";

// ── components ────────────────────────────────────────────────────────────────
import QuickActions from "./components/QuickActions";
import StorageOverview from "./components/StorageOverview";
import Toolbar from "./components/Toolbar";
import CategoryFilter from "./components/CategoryFilter";
import DocumentsSection from "./components/DocumentsSection";
import AdvancementSection from "./components/AdvancementSection";
import UploadModal from "./components/UploadModal";
import DocViewModal from "./components/DocViewModal";
import DocRequestModal from "./components/DocRequestModal";
import AdvancementModal from "./components/AdvancementModal";
import RulesModal from "./components/RulesModal";
import TermsModal from "./components/TermsModal";
import RequestItem from "./components/RequestItem";
import DuplicateDocumentModal from "./components/DuplicateDocumentModal";

// ─────────────────────────────────────────────────────────────────────────────

const Mo3tiDocumentsPage = ({ employeeData, isDarkMode }) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const t = translations[language];
  const isRTL = language === "ar";
  const screenSize = useResponsive();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { profile } = useProfile();

  // ── state ──────────────────────────────────────────────────────────────────
  const [documents, setDocuments] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedType, setSelectedType] = useState("contract");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLimits, setIsLoadingLimits] = useState(true);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateExistingDoc, setDuplicateExistingDoc] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestType, setRequestType] = useState("hr_letter");
  const [requestReason, setRequestReason] = useState("");
  const [documentRequests, setDocumentRequests] = useState([]);
  const [error, setError] = useState(null);
  const [requestRecipient, setRequestRecipient] = useState("");
  const [requestDetails, setRequestDetails] = useState("");
  const [showAdvancementModal, setShowAdvancementModal] = useState(false);
  const [advancementRequests, setAdvancementRequests] = useState([]);
  const [advancementStats, setAdvancementStats] = useState(null);
  const [advancementLimits, setAdvancementLimits] = useState(null);
  const [advancementValidation, setAdvancementValidation] = useState(null);
  const [alertModal, setAlertModal] = useState({
    open: false, title: "", message: "", variant: "info", showCancel: false, onConfirm: null,
  });
  const [showAdvancementRulesModal, setShowAdvancementRulesModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [advancementForm, setAdvancementForm] = useState({
    requestedAmount: "",
    reason: "",
    urgency: "normal",
    numberOfInstallments: "",
    repaymentStartDate: "",
  });

  // ── derived constants ──────────────────────────────────────────────────────
  const documentTypes = getDocumentTypes(t);
  const urgencyLevels = getUrgencyLevels(t);

  const categories = [
    { id: "all", name: t.allDocuments, count: documents.length, icon: FileText },
    { id: "contracts", name: t.contracts, count: documents.filter((d) => d.category === "contracts").length, icon: FileText },
    { id: "payslips", name: t.payslips, count: documents.filter((d) => d.category === "payslips").length, icon: FileSpreadsheet },
    { id: "certificates", name: t.certificates, count: documents.filter((d) => d.category === "certificates").length, icon: Star },
    { id: "policies", name: t.policies, count: documents.filter((d) => d.category === "policies").length, icon: Folder },
  ];

  const styles = buildStyles(theme, screenSize, isRTL, getStatusColor, getUrgencyColor);

  // ── responsive resize ──────────────────────────────────────────────────────
  // handled inside useResponsive hook

  // ── handlers ───────────────────────────────────────────────────────────────
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    }
  };

  const handleDocumentSelect = (docId) => {
    setSelectedDocuments((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  const handleDocumentClick = (document) => {
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const handlePreview = (documentId) => {
    const token = localStorage.getItem("token");
    const url = `${BASE_URL}/api/documents/preview/${documentId}?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setAlertModal({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: language === "ar" ? "يرجى اختيار ملف أولاً" : "Please select a file first",
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    // ── Duplicate check ───────────────────────────────────────
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const checkRes = await apiFetch(`${BASE_URL}/api/documents/check/${infinityCode}/${selectedType}`);
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        if (checkData.exists) {
          setDuplicateExistingDoc(checkData.document);
          setShowDuplicateModal(true);
          return; // pause — user must confirm in modal
        }
      }
    } catch (_) { /* non-blocking */ }

    await doUpload();
  };

  const doUpload = async () => {
    if (!selectedFile) {
      setAlertModal({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: language === "ar" ? "يرجى اختيار ملف أولاً" : "Please select a file first",
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }
    try {
      setIsUploading(true);
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("infinityCode", infinityCode);
      formData.append("type", selectedType);

      const response = await apiFetch(`${BASE_URL}/api/documents/upload`, { method: "POST", body: formData });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setShowUploadModal(false);
      setSelectedFile(null);
      setSelectedType("contract");
      setShowDuplicateModal(false);
      setDuplicateExistingDoc(null);
      await fetchDocumentRequests();
      setAlertModal({
        open: true,
        title: language === "ar" ? "تم الإرسال" : "Submitted",
        message: language === "ar"
          ? "تم رفع المستند بنجاح وهو الآن في انتظار موافقة المدير."
          : "Document uploaded and is now pending manager approval.",
        variant: "success",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
    } catch (err) {
      console.error("Upload failed:", err);
      const errorMsg = err.message || (language === "ar" ? "فشل تحميل المستند" : "Failed to upload document");
      setAlertModal({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: errorMsg,
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRequestDocument = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const employeeName = localStorage.getItem("employeeName") || employeeData?.sr;

      if (!infinityCode) {
        setError(language === "ar" ? "لم يتم العثور على رمز الموظف. يرجى تسجيل الدخول مرة أخرى." : "Employee code not found. Please log in again.");
        return;
      }

      const payload = {
        infinityCode,
        employeeName,
        requestType,
        reason: requestReason,
        recipient: requestRecipient || (language === "ar" ? "لمن يهمه الأمر" : "To Whom It May Concern"),
        details: requestDetails || "",
        clientName: profile?.clientName,
      };

      const response = await apiFetch(`${BASE_URL}/api/document-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setShowRequestModal(false);
      setRequestReason("");
      setRequestRecipient("");
      setRequestDetails("");
      setRequestType("hr_letter");
      fetchDocumentRequests();
      setAlertModal({
        open: true,
        title: language === "ar" ? "نجاح" : "Success",
        message: language === "ar" ? "تم إرسال طلب المستند بنجاح!" : "Document request submitted successfully!",
        variant: "success",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
    } catch (error) {
      console.error("Request failed:", error);
      const errorMessage = error.message || (language === "ar" ? "فشل إرسال الطلب" : "Failed to submit request");
      setError(errorMessage);
      setAlertModal({
        open: true,
        title: language === "ar" ? "خطأ" : "Error",
        message: errorMessage,
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
    }
  };

  const handleAdvancementSubmit = async () => {
    const validation = await validateAdvancementRequest();
    if (!validation) {
      setAlertModal({
        open: true,
        title: language === "ar" ? "فشل التحقق" : "Validation failed",
        message: language === "ar" ? "فشل التحقق من الطلب" : "Validation failed",
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    if (!validation.isValid) {
      const translatedErrors = validation.errors.map((error) => {
        if (error.includes("You already have") && error.includes("active advancement")) {
          const match = error.match(/You already have (\d+) active advancement\(s\)\. Maximum allowed: (\d+)/);
          if (match) {
            return language === "ar"
              ? `لديك بالفعل ${match[1]} طلب سلفة نشط. الحد الأقصى المسموح: ${match[2]}`
              : error;
          }
        }
        return error;
      });
      setAlertModal({
        open: true,
        title: language === "ar" ? "لا يمكن إرسال الطلب" : "Cannot submit request",
        message: translatedErrors.join("\n\n"),
        variant: "error",
        showCancel: false,
        onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    const proceedWithSubmission = async () => {
      try {
        if (!validation.autoApprove && advancementLimits?.enabled && !termsAccepted) {
          setShowTermsModal(true);
          return;
        }

        const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
        const employeeName = localStorage.getItem("employeeName") || employeeData?.sr;

        if (!advancementForm.requestedAmount || !advancementForm.reason) {
          setAlertModal({
            open: true,
            title: language === "ar" ? "حقل مفقود" : "Missing fields",
            message: language === "ar" ? "يرجى ملء جميع الحقول المطلوبة" : "Please fill all required fields",
            variant: "error",
            showCancel: false,
            onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
          });
          return;
        }

        const response = await apiFetch(`${BASE_URL}/api/advancement-requests`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            infinityCode,
            employeeName,
            reason: advancementForm.reason,
            urgency: advancementForm.urgency,
            requestedAmount: parseFloat(advancementForm.requestedAmount),
            numberOfInstallments: advancementForm.numberOfInstallments
              ? parseInt(advancementForm.numberOfInstallments)
              : null,
          }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        setShowAdvancementModal(false);
        setTermsAccepted(false);
        setAdvancementValidation(null);
        setAdvancementForm({ requestedAmount: "", reason: "", urgency: "normal", numberOfInstallments: "", repaymentStartDate: "" });
        fetchAdvancementRequests();
        fetchAdvancementStats();

        const message =
          data?.autoApproved || data?.data?.autoApproved
            ? language === "ar" ? "تم إرسال وموافقة طلب السلفة تلقائياً!" : "Advancement request submitted and auto-approved!"
            : language === "ar" ? "تم إرسال طلب السلفة بنجاح!" : "Advancement request submitted successfully!";

        setAlertModal({
          open: true,
          title: language === "ar" ? "نجاح" : "Success",
          message,
          variant: "success",
          showCancel: false,
          onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
        });
      } catch (error) {
        console.error("Advancement request failed:", error);
        const errorMsg = error.message || (language === "ar" ? "فشل إرسال طلب السلفة" : "Failed to submit advancement request");
        setError(errorMsg);
        setAlertModal({
          open: true,
          title: language === "ar" ? "خطأ" : "Error",
          message: errorMsg,
          variant: "error",
          showCancel: false,
          onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
        });
      }
    };

    if (validation.warnings && validation.warnings.length > 0) {
      setAlertModal({
        open: true,
        title: language === "ar" ? "تحذيرات" : "Warnings",
        message: validation.warnings.join("\n") + "\n\n" + (language === "ar" ? "هل تريد المتابعة؟" : "Do you want to continue?"),
        variant: "info",
        showCancel: true,
        onConfirm: () => { setAlertModal((prev) => ({ ...prev, open: false })); proceedWithSubmission(); },
        onClose: () => setAlertModal((prev) => ({ ...prev, open: false })),
      });
      return;
    }

    await proceedWithSubmission();
  };

  // ── API calls ───────────────────────────────────────────────────────────────
  const fetchDocuments = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const response = await apiFetch(`${BASE_URL}/api/documents/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDocuments((data?.data || data || []).map((d) => transformDocumentData(d, t)));
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    }
  };

  const fetchDocumentRequests = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const response = await apiFetch(`${BASE_URL}/api/document-requests/employee/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDocumentRequests(data?.data || data || []);
    } catch (err) {
      console.error("Failed to fetch requests:", err);
    }
  };

  const fetchAdvancementRequests = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const response = await apiFetch(`${BASE_URL}/api/advancement-requests/employee/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setAdvancementRequests(data?.data || data || []);
    } catch (err) {
      console.error("Failed to fetch advancement requests:", err);
    }
  };

  const fetchAdvancementStats = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      const response = await apiFetch(`${BASE_URL}/api/advancement-requests/stats/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setAdvancementStats(data?.data || data || null);
    } catch (err) {
      console.error("Failed to fetch advancement stats:", err);
    }
  };

  const fetchAdvancementLimits = async () => {
    try {
      setIsLoadingLimits(true);
      let infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      if (!infinityCode) {
        setAdvancementLimits({ enabled: false, message: language === "ar" ? "لم يتم العثور على معلومات الموظف" : "Employee information not found" });
        return;
      }
      infinityCode = String(infinityCode).trim();
      const response = await apiFetch(`${BASE_URL}/api/advancement-requests/limits/${infinityCode}`, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setAdvancementLimits(data?.data || data || { enabled: false });
    } catch (err) {
      console.error("Failed to fetch advancement limits:", err);
      setAdvancementLimits({ enabled: false, message: err.message || (language === "ar" ? "فشل تحميل قواعد السلفة" : "Failed to load advancement rules") });
    } finally {
      setIsLoadingLimits(false);
    }
  };

  const validateAdvancementRequest = async () => {
    try {
      const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
      if (!advancementForm.requestedAmount || !advancementForm.numberOfInstallments) return null;

      const response = await apiFetch(`${BASE_URL}/api/advancement-requests/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          infinityCode,
          requestedAmount: parseFloat(advancementForm.requestedAmount),
          numberOfInstallments: parseInt(advancementForm.numberOfInstallments),
          urgency: advancementForm.urgency || "normal",
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const payload = data?.data || data;
      setAdvancementValidation(payload);
      return payload;
    } catch (err) {
      console.error("Validation failed:", err);
      setError(err.message || (language === "ar" ? "فشل التحقق من الطلب" : "Validation failed"));
      return null;
    }
  };

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const infinityCode = localStorage.getItem("infinityCode") || employeeData?.infinityCode;
        if (!infinityCode) { setIsLoading(false); return; }

        await fetchDocuments();
        await fetchDocumentRequests();
        await fetchAdvancementRequests();
        await fetchAdvancementStats();
        await fetchAdvancementLimits();
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError(language === "ar" ? "فشل تحميل البيانات" : "Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [employeeData?.infinityCode, language]);

  // Real-time validation debounce
  useEffect(() => {
    if (advancementForm.requestedAmount && advancementForm.numberOfInstallments) {
      const timer = setTimeout(() => { validateAdvancementRequest(); }, 500);
      return () => clearTimeout(timer);
    } else {
      setAdvancementValidation(null);
    }
  }, [advancementForm.requestedAmount, advancementForm.numberOfInstallments, advancementForm.urgency]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (showAdvancementModal && advancementForm.requestedAmount && advancementForm.numberOfInstallments) {
        validateAdvancementRequest();
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [advancementForm.requestedAmount, advancementForm.numberOfInstallments, advancementForm.urgency, showAdvancementModal]);

  // ── derived values ─────────────────────────────────────────────────────────
  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      (doc.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (doc.tags || []).some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === "all" || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const totalUsedMB = calculateTotalStorage(documents);
  const totalLimitMB = 30;
  const storageStats = {
    used: totalUsedMB,
    total: totalLimitMB,
    percentage: totalLimitMB > 0 ? Math.round((totalUsedMB / totalLimitMB) * 100 * 100) / 100 : 0,
  };

  // ── shared helpers to pass down ────────────────────────────────────────────
  const helpers = {
    formatCurrency: (amount) => formatCurrency(amount, language),
    formatDate: (dateString) => formatDate(dateString, language),
    getStatusColor: (status) => getStatusColor(status, t),
    getStatusIcon: (status) => getStatusIcon(status, t),
    getFileIcon,
    getUrgencyColor: (urgency) => getUrgencyColor(urgency, urgencyLevels),
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="employee-selfservice-root" style={styles.pageContainer}>
      <div style={styles.container}>

        {/* Page Header */}
        <div style={styles.profileHeader  }>
          <h1 style={styles.pageTitle}>{t.pageTitle}</h1>
        </div>

        {/* Quick Actions */}
        <QuickActions t={t} styles={styles} scrollToSection={scrollToSection} />

        {/* Storage Overview */}
        <StorageOverview
          t={t}
          styles={styles}
          theme={theme}
          storageStats={storageStats}
          documents={documents}
        />

        {/* Toolbar */}
        <Toolbar
          t={t}
          styles={styles}
          theme={theme}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setShowRequestModal={setShowRequestModal}
          setShowUploadModal={setShowUploadModal}
        />

        {/* Category Filter */}
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          styles={styles}
          theme={theme}
        />

        {/* Documents Section */}
        <DocumentsSection
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          filteredDocuments={filteredDocuments}
          selectedDocuments={selectedDocuments}
          getFileIcon={getFileIcon}
          formatDate={helpers.formatDate}
          handleDocumentClick={handleDocumentClick}
        />

        {/* Document Requests */}
        <div style={styles.card1}>
          <h3 style={styles.cardTitle}>
            <Send size={28} />
            {t.myRequests} ({documentRequests.length})
          </h3>
          {documentRequests.length === 0 ? (
            <div style={styles.emptyState}>
              <Send size={80} style={styles.emptyStateIcon} />
              <p style={styles.emptyStateText}>{t.noRequestsYet}</p>
            </div>
          ) : (
            <div>
              {documentRequests.map((request) => (
                <RequestItem
                  key={request.id}
                  request={request}
                  styles={styles}
                  theme={theme}
                  getStatusIcon={helpers.getStatusIcon}
                  getStatusColor={helpers.getStatusColor}
                  formatDate={helpers.formatDate}
                  documentTypes={documentTypes}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>

        {/* Advancement Section */}
        <AdvancementSection
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          advancementStats={advancementStats}
          advancementRequests={advancementRequests}
          advancementLimits={advancementLimits}
          isLoadingLimits={isLoadingLimits}
          urgencyLevels={urgencyLevels}
          formatCurrency={helpers.formatCurrency}
          formatDate={helpers.formatDate}
          getStatusColor={helpers.getStatusColor}
          getStatusIcon={helpers.getStatusIcon}
          getUrgencyColor={helpers.getUrgencyColor}
          setShowAdvancementRulesModal={setShowAdvancementRulesModal}
          setAlertModal={setAlertModal}
        />
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      {showUploadModal && (
        <UploadModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          setShowUploadModal={setShowUploadModal}
          handleUpload={handleUpload}
          isUploading={isUploading}
          ReusableDropdown={ReusableDropdown}
        />
      )}

      {showDocumentModal && selectedDocument && (
        <DocViewModal
          t={t}
          styles={styles}
          theme={theme}
          selectedDocument={selectedDocument}
          setShowDocumentModal={setShowDocumentModal}
          handlePreview={handlePreview}
          formatDate={helpers.formatDate}
          screenSize={screenSize}
        />
      )}

      {showRequestModal && (
        <DocRequestModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          isRTL={isRTL}
          documentTypes={documentTypes}
          requestType={requestType}
          setRequestType={setRequestType}
          requestDetails={requestDetails}
          setRequestDetails={setRequestDetails}
          requestRecipient={requestRecipient}
          setRequestRecipient={setRequestRecipient}
          requestReason={requestReason}
          setRequestReason={setRequestReason}
          setShowRequestModal={setShowRequestModal}
          handleRequestDocument={handleRequestDocument}
        />
      )}

      {showAdvancementModal && (
        <AdvancementModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          isRTL={isRTL}
          advancementForm={advancementForm}
          setAdvancementForm={setAdvancementForm}
          advancementLimits={advancementLimits}
          advancementValidation={advancementValidation}
          setShowAdvancementModal={setShowAdvancementModal}
          handleAdvancementSubmit={handleAdvancementSubmit}
          formatCurrency={helpers.formatCurrency}
          setAlertModal={setAlertModal}
        />
      )}

      {showAdvancementRulesModal && advancementLimits && (
        <RulesModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          isRTL={isRTL}
          advancementLimits={advancementLimits}
          setShowAdvancementRulesModal={setShowAdvancementRulesModal}
          setShowAdvancementModal={setShowAdvancementModal}
          formatCurrency={helpers.formatCurrency}
        />
      )}

      {showTermsModal && advancementLimits && (
        <TermsModal
          t={t}
          styles={styles}
          theme={theme}
          language={language}
          isRTL={isRTL}
          advancementLimits={advancementLimits}
          termsAccepted={termsAccepted}
          setTermsAccepted={setTermsAccepted}
          setShowTermsModal={setShowTermsModal}
          handleAdvancementSubmit={handleAdvancementSubmit}
          formatCurrency={helpers.formatCurrency}
        />
      )}


      {/* Alert Modal */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        showCancel={alertModal.showCancel}
        onConfirm={alertModal.onConfirm}
        onClose={() => setAlertModal((prev) => ({ ...prev, open: false }))}
        theme={theme}
        language={language}
      />

      {/* Duplicate document warning */}
      <DuplicateDocumentModal
        open={showDuplicateModal}
        onClose={() => { setShowDuplicateModal(false); setDuplicateExistingDoc(null); }}
        onConfirm={() => { setShowDuplicateModal(false); doUpload(); }}
        existingDoc={duplicateExistingDoc}
        newFile={selectedFile}
        selectedType={selectedType}
        language={language}
        theme={theme}
        onPreviewExisting={() => duplicateExistingDoc?.id && handlePreview(duplicateExistingDoc.id)}
      />
    </div>
  );
};

export default Mo3tiDocumentsPage;